import React, { useEffect, useState } from "react";
import { AccountingAccount } from "../services/accounting-account-service";
import { AccountingAccountCreate } from "../components/accounting-account-create";
import { AccountingAccountDetail } from "../components/accounting-account-detail";
import { AccountingAccountFilter } from "../components/accounting-account-filter";
import { ActiveAccountingAccountTab } from "./active-accounting-account-tab";
import { InactiveAccountingAccountTab } from "./inactive-accounting-account-tab";
import { AccountingAccountService } from "../services/accounting-account-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

function useDebounce<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function AccountingAccountIndexPage() {
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [viewItem, setViewItem] = useState<AccountingAccount | null>(null);
  const [editItem, setEditItem] = useState<AccountingAccount | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);
  const { fetch: createAccount } = AccountingAccountService.create();
  const { fetch: updateAccount } = AccountingAccountService.update();
  const { fetch: countTab } = AccountingAccountService.countTab();

  useEffect(() => {
    const run = async () => {
      const params: any = {};
      if (debouncedSearch) params.key_search = debouncedSearch;
      if (typeFilter) params.type = Number(typeFilter);
      const res = await countTab(params);
      if (res && isSuccess(res)) {
        const d: any = res.data ?? {};
        setActiveCount(Number(d.active ?? 0));
        setInactiveCount(Number(d.inactive ?? 0));
      } else {
        setActiveCount(0);
        setInactiveCount(0);
      }
    };
    void run();
  }, [debouncedSearch, typeFilter]);

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 font-sans space-y-6">
      
      {/* --- HEADER TITLE & ACTION --- */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
           <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">
             Tài khoản kế toán
           </h1>
        </div>
        
        <button 
          onClick={() => { setEditItem(null); setOpenForm(true); }} 
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM TÀI KHOẢN</span>
        </button>
      </div>

      {/* --- MAIN CARD --- */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        
        {/* --- TOOLBAR (Tabs & Filters) --- */}
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
          
          {/* LEFT: TABS */}
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setTab("active")} 
              className={`group relative flex items-center gap-2.5 pb-3 text-sm font-bold uppercase tracking-wide transition-all ${
                tab === "active" ? "text-green-700" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span>Đang hoạt động</span>
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold transition-colors ${
                 tab === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {activeCount}
              </span>
              {/* Active Indicator Line */}
              <span className={`absolute bottom-0 left-0 h-[3px] w-full rounded-t-full bg-green-600 transition-transform duration-300 ${
                 tab === "active" ? "scale-x-100" : "scale-x-0"
              }`} />
            </button>

            <button 
              onClick={() => setTab("inactive")} 
              className={`group relative flex items-center gap-2.5 pb-3 text-sm font-bold uppercase tracking-wide transition-all ${
                tab === "inactive" ? "text-green-700" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span>Tạm ngưng</span>
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold transition-colors ${
                 tab === "inactive" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {inactiveCount}
              </span>
              {/* Active Indicator Line */}
              <span className={`absolute bottom-0 left-0 h-[3px] w-full rounded-t-full bg-green-600 transition-transform duration-300 ${
                 tab === "inactive" ? "scale-x-100" : "scale-x-0"
              }`} />
            </button>
          </div>

          {/* RIGHT: FILTERS */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            
            {/* 1. Filter Component */}
            <div className="relative w-full sm:w-auto min-w-[200px]">
               {/* Icon Phễu */}
               <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 z-10">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z" clipRule="evenodd" />
                  </svg>
               </div>
               
               {/* Wrapper để style component con */}
               <div className="[&>select]:pl-9 [&>select]:w-full [&>select]:rounded-xl [&>select]:border-gray-200 [&>select]:bg-gray-50 [&>select]:py-2.5 [&>select]:text-sm [&>select]:font-medium [&>select]:text-gray-700 [&>select]:shadow-sm focus-within:[&>select]:ring-4 focus-within:[&>select]:ring-green-500/10 focus-within:[&>select]:border-green-500 focus-within:[&>select]:bg-white hover:[&>select]:bg-gray-100 transition-all">
                  <AccountingAccountFilter value={typeFilter} onChange={(v) => setTypeFilter(v)} />
               </div>
            </div>

            {/* 2. Search Input */}
            <div className="relative w-full sm:w-[300px]">
               <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                  </svg>
               </div>
               <input 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="Tìm kiếm tài khoản..." 
                  className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all" 
               />
            </div>
          </div>
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="p-0">
          {tab === "active" ? (
            <ActiveAccountingAccountTab
              key={reloadKey + "-active"}
              search={debouncedSearch}
              typeFilter={typeFilter}
              onEdit={(item) => { setEditItem(item); setOpenForm(true); }}
              onView={(item) => setViewItem(item)}
              onLoadedCount={(n) => setActiveCount(n)}
            />
          ) : (
            <InactiveAccountingAccountTab
              key={reloadKey + "-inactive"}
              search={debouncedSearch}
              typeFilter={typeFilter}
              onEdit={(item) => { setEditItem(item); setOpenForm(true); }}
              onView={(item) => setViewItem(item)}
              onLoadedCount={(n) => setInactiveCount(n)}
            />
          )}
        </div>
      </div>

      {/* --- MODALS --- */}
      <AccountingAccountCreate
        open={openForm}
        item={editItem}
        onClose={() => setOpenForm(false)}
        onSave={async (payload) => {
          let res;
          if (editItem) res = await updateAccount(editItem.id, payload);
          else res = await createAccount(payload);
          if (res && isSuccess(res)) {
            showToast({ message: editItem ? "Cập nhật thành công" : "Tạo mới thành công", type: "success" });
            setOpenForm(false);
            setEditItem(null);
            setReloadKey((k) => k + 1);
          } else {
            showToast({ message: res?.message ?? "Không thể lưu dữ liệu", type: "error" });
          }
        }}
      />
      <AccountingAccountDetail open={!!viewItem} data={viewItem} onClose={() => setViewItem(null)} />
    </div>
  );
}